import React, { useMemo, useState } from "react";
import { X, RefreshCcw } from "lucide-react";

/**
 * Modal de réinitialisation sélective des assignations d'arbitres.
 *
 * Remet `referee1_id` et `referee2_id` à null pour les parties matchant les filtres.
 * Le marqueur (`scorer_id`) est PRÉSERVÉ.
 *
 * Props:
 *  - games: array — liste actuelle (toutes parties + pauses, le composant filtre)
 *  - onClose()
 *  - onConfirm(newGames: array) — appelé avec la nouvelle liste à sauver
 */
export default function ResetRefereesModal({ games, onClose, onConfirm }) {
  const playable = useMemo(() => games.filter((g) => !g.is_break), [games]);
  const allDates = useMemo(
    () => [...new Set(playable.map((g) => g.date).filter(Boolean))].sort(),
    [playable]
  );
  const allSurfaces = useMemo(
    () => [...new Set(playable.map((g) => String(g.surface || "")).filter(Boolean))].sort(),
    [playable]
  );

  const [scope, setScope] = useState("all"); // all | partial
  const [selDates, setSelDates] = useState([]);
  const [selSurfaces, setSelSurfaces] = useState([]);

  const toggle = (arr, setArr, value) => {
    if (arr.includes(value)) setArr(arr.filter((x) => x !== value));
    else setArr([...arr, value]);
  };

  const matches = (g) => {
    if (g.is_break) return false;
    if (scope === "all") return true;
    if (selDates.length === 0 && selSurfaces.length === 0) return false;
    const dateOk = selDates.length === 0 || selDates.includes(g.date || "");
    const surfaceOk = selSurfaces.length === 0 || selSurfaces.includes(String(g.surface || ""));
    return dateOk && surfaceOk;
  };

  const willReset = games.filter(matches);
  const willResetCount = willReset.length;
  const assignmentsRemoved = willReset.reduce(
    (n, g) => n + (g.referee1_id ? 1 : 0) + (g.referee2_id ? 1 : 0),
    0
  );
  const partialNoSelection =
    scope === "partial" && selDates.length === 0 && selSurfaces.length === 0;

  const handleConfirm = () => {
    if (assignmentsRemoved === 0) return;
    const next = games.map((g) =>
      matches(g) ? { ...g, referee1_id: null, referee2_id: null } : g
    );
    onConfirm(next);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="reset-referees-modal"
    >
      <div
        className="rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]"
          aria-label="Fermer"
          data-testid="reset-modal-close"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Réinitialisation sélective</div>
        <h3 className="text-xl font-bold tracking-tight mb-1">Vider les arbitres assignés</h3>
        <p className="text-xs text-[var(--muted)] mb-4 leading-relaxed">
          Remet <strong>Arbitre 1</strong> et <strong>Arbitre 2</strong> à "non assigné" sur les parties choisies.
          Les <strong>marqueurs</strong> sont préservés.
        </p>

        {/* Scope toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setScope("all")}
            className={`p-3 rounded-xl border-2 text-left transition-colors ${
              scope === "all" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"
            }`}
            data-testid="reset-scope-all"
          >
            <div className="font-bold text-sm">Tout l'horaire</div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Toutes les parties</div>
          </button>
          <button
            type="button"
            onClick={() => setScope("partial")}
            className={`p-3 rounded-xl border-2 text-left transition-colors ${
              scope === "partial" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"
            }`}
            data-testid="reset-scope-partial"
          >
            <div className="font-bold text-sm">Réinit. ciblée</div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Par date et/ou surface</div>
          </button>
        </div>

        {scope === "partial" && (
          <div className="space-y-4 mb-4">
            {allDates.length > 0 && (
              <div>
                <div className="label mb-2">Date(s)</div>
                <div className="flex flex-wrap gap-2">
                  {allDates.map((d) => {
                    const active = selDates.includes(d);
                    const count = playable.filter((g) => g.date === d).length;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggle(selDates, setSelDates, d)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium tabular border transition-colors ${
                          active
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-[var(--text)] border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
                        }`}
                        data-testid={`reset-date-${d}`}
                      >
                        {d} <span className="opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {allSurfaces.length > 0 && (
              <div>
                <div className="label mb-2">Surface(s)</div>
                <div className="flex flex-wrap gap-2">
                  {allSurfaces.map((s) => {
                    const active = selSurfaces.includes(s);
                    const count = playable.filter((g) => String(g.surface || "") === s).length;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggle(selSurfaces, setSelSurfaces, s)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium tabular border transition-colors ${
                          active
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-[var(--text)] border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
                        }`}
                        data-testid={`reset-surface-${s}`}
                      >
                        Surface {s} <span className="opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {selDates.length > 0 && selSurfaces.length > 0 && (
              <p className="text-xs text-[var(--muted)] leading-relaxed">
                ℹ Les parties doivent matcher <strong>une</strong> date sélectionnée <strong>ET</strong> une surface sélectionnée.
              </p>
            )}
          </div>
        )}

        {/* Preview */}
        <div
          className="rounded-xl p-4 mb-4"
          style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
        >
          {partialNoSelection ? (
            <p className="text-sm text-[var(--muted)]">
              Sélectionne au moins une date ou une surface pour activer la réinitialisation.
            </p>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-bold tabular" data-testid="reset-preview-games">
                  {willResetCount}
                </span>
                <span className="text-sm text-[var(--muted)]">
                  partie{willResetCount > 1 ? "s" : ""} impactée{willResetCount > 1 ? "s" : ""}
                </span>
              </div>
              <div className="text-xs text-[var(--muted)]">
                <span
                  className={assignmentsRemoved > 0 ? "text-[var(--text)] font-semibold" : ""}
                  data-testid="reset-preview-assignments"
                >
                  {assignmentsRemoved} assignation{assignmentsRemoved > 1 ? "s" : ""} d'arbitre
                </span>
                {" "}sera{assignmentsRemoved > 1 ? "ont" : ""} retirée{assignmentsRemoved > 1 ? "s" : ""}.
                {assignmentsRemoved === 0 && willResetCount > 0 && (
                  <> Aucune affectation à retirer.</>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="reset-cancel-btn">
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className="btn-danger"
            disabled={assignmentsRemoved === 0}
            data-testid="reset-confirm-btn"
          >
            <RefreshCcw size={14} strokeWidth={2.5} /> Réinitialiser {assignmentsRemoved > 0 ? `(${assignmentsRemoved})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
